import { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { ApiError } from '@/auth/api';
import { useAuth } from '@/auth/useAuth';
import { NarrowPage } from '@/components/NarrowPage';
import { PageHeader } from '@/components/PageHeader';
import { SectionCard } from '@/components/SectionCard';
import { getMyReservation, type BuyerReservation } from '@/features/account/api';
import { describeReservedDevice, reservationConditions } from '@/features/account/reservations';
import { formatHistoryTimestamp } from '@/features/operations/labels';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';
import { StatusChip } from '@/components/StatusChip';

type PageState =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'error' }
  | { status: 'ready'; reservation: BuyerReservation };

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="p">
        {label}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </Box>
  );
}

/**
 * One reservation of the buyer (VEN-3): where reserving lands, and where the mail links to.
 * Says what was reserved, until when, at what price and what happens next.
 */
export function CustomerReservationPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const { token } = useAuth();
  const { formatNullablePrice } = usePriceDisplay();
  const justReserved =
    typeof location.state === 'object' &&
    location.state !== null &&
    'justReserved' in location.state &&
    location.state.justReserved === true;
  const [state, setState] = useState<PageState>({ status: 'loading' });

  const load = useCallback(() => {
    if (!token) return;
    setState({ status: 'loading' });
    getMyReservation(token, id)
      .then((reservation) => setState({ status: 'ready', reservation }))
      .catch((error: unknown) =>
        setState({
          status: error instanceof ApiError && error.status === 404 ? 'missing' : 'error',
        }),
      );
  }, [token, id]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <NarrowPage maxWidth={640}>
      <PageHeader
        eyebrow="TU RESERVA"
        title={justReserved ? '¡Listo, lo reservaste!' : 'Tu reserva'}
        description="Con la referencia, PhoneTec identifica tu reserva cuando te contacta."
      />
      {state.status === 'loading' ? (
        <Stack direction="row" spacing={2} alignItems="center" role="status">
          <CircularProgress size={24} color="secondary" />
          <Typography>Cargando la reserva...</Typography>
        </Stack>
      ) : null}
      {state.status === 'missing' ? (
        <Alert severity="error">
          No encontramos esa reserva en tu cuenta.{' '}
          <Button component={RouterLink} to="/account" size="small" color="inherit">
            Ver mis reservas
          </Button>
        </Alert>
      ) : null}
      {state.status === 'error' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={load}>
              Reintentar
            </Button>
          }
        >
          No pudimos cargar la reserva.
        </Alert>
      ) : null}
      {state.status === 'ready' ? (
        <SectionCard title={describeReservedDevice(state.reservation.device)}>
          <Stack spacing={2}>
            {justReserved ? (
              <Alert severity="success">Te mandamos un mail con los datos de la reserva.</Alert>
            ) : null}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="h5" component="p">
                Reserva #{state.reservation.reference}
              </Typography>
              <StatusChip domain="reservation" status={state.reservation.status} />
            </Stack>
            <Detail
              label="Precio reservado"
              value={formatNullablePrice(state.reservation.priceMinor)}
            />
            <Detail
              label={state.reservation.status === 'EXPIRED' ? 'Venció el' : 'Vence el'}
              value={formatHistoryTimestamp(state.reservation.expiresAt)}
            />
            {state.reservation.status === 'ACTIVE' ? (
              <Alert severity="info">
                <strong>Próximos pasos.</strong> {reservationConditions}
              </Alert>
            ) : null}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <Button component={RouterLink} to="/account" variant="outlined" color="inherit">
                Ver mis reservas
              </Button>
              <Button component={RouterLink} to="/catalog" color="inherit">
                Seguir viendo el catálogo
              </Button>
            </Stack>
          </Stack>
        </SectionCard>
      ) : null}
    </NarrowPage>
  );
}
